import { useEffect, useState } from "react";
import { useTodayReflection } from "@/lib/useTodayReflection";
import { useTrack } from "@/lib/track";

/**
 * Evening Reflection — a single quiet prompt + freeform response area, anchored
 * to the user's local calendar day. Saving updates the same row (one
 * reflection per day). Designed to feel like closing a notebook, not opening
 * a productivity app: no character counter, no streaks, no celebration on save.
 */
export function EveningReflection() {
  const { query, save } = useTodayReflection();
  const track = useTrack();
  const [draft, setDraft] = useState("");
  const [hasEdited, setHasEdited] = useState(false);

  const prompt = query.data?.prompt;
  const existing = query.data?.reflection ?? null;

  // Hydrate draft once when the existing reflection loads. Don't overwrite
  // mid-typing if the user has already started editing.
  useEffect(() => {
    if (!hasEdited && existing?.body) setDraft(existing.body);
  }, [existing?.body, hasEdited]);

  const trimmed = draft.trim();
  const isUnchanged = trimmed === (existing?.body ?? "").trim();
  const canSave = trimmed.length > 0 && !isUnchanged && !save.isPending;

  const handleSave = async () => {
    await save.mutateAsync(trimmed);
    track("reflection.save", {
      category: prompt?.category ?? "reflection",
      updated: Boolean(existing),
    });
    setHasEdited(false);
  };

  const theme = query.data?.recent_theme ?? null;
  const themeMatches = theme && prompt && theme.category === prompt.category;

  return (
    <article className="panel p-10 animate-fade-up">
      <p className="label-eyebrow text-gold-glow">{prompt?.category ?? "reflection"}</p>
      <h2 className="mt-3 font-display text-2xl tracking-tight leading-tight max-w-2xl">
        {prompt?.text ?? "Tonight"}
      </h2>
      <p className="mt-3 text-sm text-whisper italic">A quiet close to your day.</p>
      {theme && (
        <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
          {themeMatches
            ? `A theme you've returned to recently · ${theme.count} this week`
            : `You've been reflecting on ${theme.category} lately · ${theme.count} this week`}
        </p>
      )}

      <div className="mt-8">
        <label htmlFor="evening-reflection" className="sr-only">
          Your reflection
        </label>
        <textarea
          id="evening-reflection"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setHasEdited(true);
          }}
          placeholder="Write whatever comes."
          rows={6}
          className="w-full bg-ink-elev/40 border border-veil/40 rounded-lg px-4 py-3 text-fog placeholder:text-muted leading-relaxed focus:outline-none focus:border-gold/40 resize-y"
        />
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
            {existing
              ? `Saved ${timeAgo(existing.updated_at)} · only you can see this`
              : "Only you can see this"}
          </p>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-veil/60 text-fog hover:border-gold/50 hover:text-gold-glow transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {save.isPending
              ? "Saving…"
              : existing
                ? isUnchanged ? "Saved" : "Update"
                : "Save reflection"}
          </button>
        </div>
      </div>
    </article>
  );
}

function timeAgo(ts: number): string {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
